import React from 'react';
import { AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';

const RELOAD_KEY = 'cadence:update-reload-at';
const RELOAD_WINDOW_MS = 60_000;

/**
 * A page's code failed to download. After a release, a tab opened before it
 * asks for page files that no longer exist; reloading picks up the new ones.
 */
export function isChunkLoadError(error: unknown): boolean {
  const e = error as { name?: string; message?: string } | null;
  if (!e) return false;
  const message = e.message ?? '';
  return e.name === 'ChunkLoadError'
    || /Loading (CSS )?chunk [\w-]+ failed/i.test(message)
    || /Failed to fetch dynamically imported module/i.test(message);
}

/** Reload once per minute at most, so a file that's really missing shows the card rather than looping. */
function claimReload(): boolean {
  try {
    const last = Number(sessionStorage.getItem(RELOAD_KEY) || 0);
    if (Date.now() - last < RELOAD_WINDOW_MS) return false;
    sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
    return true;
  } catch {
    return false;
  }
}

interface Props {
  children: React.ReactNode;
  /** How to reload the page (tests pass their own). */
  reload?: () => void;
}

interface State {
  error: Error | null;
}

/**
 * Catches a page that fails to render, so one broken page no longer blanks
 * the whole app. Used around the router and around each page in the app
 * shell (keyed by route there, so moving to another page clears it).
 */
export class ErrorBoundary extends React.Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    if (isChunkLoadError(error) && claimReload()) {
      this.reload();
      return;
    }
    console.error('A page failed to render', error, info.componentStack);
  }

  private reload = () => (this.props.reload ?? (() => window.location.reload()))();

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    const updated = isChunkLoadError(error);
    return (
      <div role="alert" className="mx-auto mt-10 flex max-w-md flex-col items-center gap-3 px-4 text-center">
        <AlertTriangle className="h-8 w-8 text-muted-foreground" aria-hidden />
        <h2 className="text-lg font-semibold">
          {updated ? 'Cadence has been updated.' : 'Something went wrong on this page.'}
        </h2>
        <p className="text-sm text-muted-foreground">
          {updated
            ? 'Reload to get the latest version.'
            : 'Reloading usually fixes it. If it keeps happening, use Report a problem in your account menu.'}
        </p>
        <div className="flex gap-2">
          <Button onClick={this.reload}>Reload</Button>
          <Button variant="ghost" asChild>
            <a href="/">Go to Today</a>
          </Button>
        </div>
      </div>
    );
  }
}
